/**
 * Bangla / English. Keys are the English text; `bn.ts` holds the Bangla for every key
 * (scripts/i18n-check.mjs fails the build if any key is missing). Numbers, dates and
 * taka amounts switch to Bangla digits and month names in Bangla mode.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { BN } from "./bn";

export type Lang = "bn" | "en";
const DIG = "০১২৩৪৫৬৭৮৯";
const MON = { en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"], bn: ["জানু", "ফেব্রু", "মার্চ", "এপ্রি", "মে", "জুন", "জুলা", "আগ", "সেপ্টে", "অক্টো", "নভে", "ডিসে"] };
const MONTH = { en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], bn: ["জানুয়ারি", "ফেব্রুয়ারি", "মার্চ", "এপ্রিল", "মে", "জুন", "জুলাই", "আগস্ট", "সেপ্টেম্বর", "অক্টোবর", "নভেম্বর", "ডিসেম্বর"] };
const DOW = { en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], bn: ["রবি", "সোম", "মঙ্গল", "বুধ", "বৃহঃ", "শুক্র", "শনি"] };

export interface I18n {
  lang: Lang; setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  d: (s: string | number) => string;                      // digits
  num: (n: number, dec?: number) => string;
  tk: (n: number, dec?: number) => string;                 // ৳1,23,456
  date: (iso: string | Date | null | undefined) => string; // 20 Oct 2026
  dShort: (iso: string) => string;                         // 20 Oct
  month: (m: number) => string; monthShort: (m: number) => string; dow: (i: number) => string;
}
const Ctx = createContext<I18n | null>(null);

export function makeI18n(lang: Lang, setLang: (l: Lang) => void): I18n {
  const d = (s: string | number) => (lang === "bn" ? String(s).replace(/\d/g, (x) => DIG[+x]) : String(s));
  const t = (key: string, vars?: Record<string, string | number>) => {
    let s = lang === "bn" ? BN[key] ?? key : key;
    if (lang === "bn" && BN[key] == null && /[a-z]{2}/.test(key)) ((globalThis as any).__kormiMissing ??= new Set<string>()).add(key);
    if (vars) s = s.replace(/\{(\w+)\}/g, (_, k) => { const v = vars[k]; return v == null ? "" : typeof v === "number" ? d(v.toLocaleString("en-IN")) : String(v); });
    return s;
  };
  const num = (n: number, dec = 0) => d(Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec }));
  const toD = (v: string | Date) => (typeof v === "string" ? new Date(v.length === 10 ? v + "T00:00:00Z" : v) : v);
  return {
    lang, setLang, t, d, num,
    tk: (n, dec = 0) => d("৳" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec })),
    date: (v) => { if (!v) return "—"; const x = toD(v); return d(`${String(x.getUTCDate()).padStart(2, "0")} ${MON[lang][x.getUTCMonth()]} ${x.getUTCFullYear()}`); },
    dShort: (v) => { const x = toD(v); return d(`${x.getUTCDate()} ${MON[lang][x.getUTCMonth()]}`); },
    month: (m) => MONTH[lang][m - 1], monthShort: (m) => MON[lang][m - 1], dow: (i) => DOW[lang][i],
  };
}

export function I18nProvider({ children, initial }: { children: ReactNode; initial: Lang }) {
  const [lang, setLangState] = useState<Lang>(initial);
  const setLang = (l: Lang) => { setLangState(l); try { localStorage.setItem("kormi:lang", l); } catch {} document.documentElement.lang = l; };
  const v = useMemo(() => makeI18n(lang, setLang), [lang]);
  document.documentElement.lang = lang;
  return <Ctx.Provider value={v}>{children}</Ctx.Provider>;
}
export const useI18n = () => { const c = useContext(Ctx); if (!c) throw new Error("I18nProvider missing"); return c; };
